import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';

interface TinyAccountInfo {
  razao_social?: string;
  cnpj_cpf?: string;
  fantasia?: string;
  email?: string;
  regime_tributario?: string;
}


export function TinyERPPage() {
  const [tinyToken, setTinyToken] = useState('');
  const [tinyConnected, setTinyConnected] = useState(false);
  const [tinyLoading, setTinyLoading] = useState(false);
  const [tinyAccountInfo, setTinyAccountInfo] = useState<TinyAccountInfo | null>(null);


  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId =
      localStorage.getItem('superadmin_selected_tenant') ||
      localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Load token from backend settings on mount
  useEffect(() => {
    const loadTinySettings = async () => {
      try {
        const response = await authenticatedFetch('/api/settings');
        if (response.ok) {
          const data = await response.json();
          if (data.tinyApiTokenConfigured || data.tinyApiToken) {
            if (data.tinyApiToken && data.tinyApiToken !== '****************') {
              setTinyToken(data.tinyApiToken);
            }
            setTinyConnected(true);
            // Try to load cached account info from localStorage
            const savedAccountInfo = localStorage.getItem('tiny_account_info');
            if (savedAccountInfo) {
              try {
                setTinyAccountInfo(JSON.parse(savedAccountInfo));
              } catch (e) {
                console.error('Erro ao parsear info da conta Tiny:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do Tiny:', error);
      }
    };
    loadTinySettings();
  }, []);

  const handleConnectTiny = async () => {
    if (!tinyToken.trim()) {
      toast.error('Informe o token da API do Tiny');
      return;
    }

    setTinyLoading(true);
    try {
      const response = await authenticatedFetch('/api/tiny/info', {
        method: 'POST',
        body: JSON.stringify({ token: tinyToken.trim() }),
      });

      const data = await response.json();

      if (data?.retorno?.status === 'OK' && data?.retorno?.conta) {
        const conta = data.retorno.conta;
        const accountInfo: TinyAccountInfo = {
          razao_social: conta.razao_social,
          cnpj_cpf: conta.cnpj_cpf,
          fantasia: conta.fantasia,
          email: conta.email,
          regime_tributario: conta.regime_tributario,
        };
        setTinyAccountInfo(accountInfo);
        setTinyConnected(true);
        localStorage.setItem('tiny_account_info', JSON.stringify(accountInfo));
        // Save token to backend (per tenant)
        await authenticatedFetch('/api/settings', {
          method: 'PUT',
          body: JSON.stringify({ tinyApiToken: tinyToken.trim() }),
        });
        toast.success('Conectado ao Tiny ERP com sucesso!');
      } else {
        const errorMsg = data?.retorno?.erros?.[0]?.erro || data?.error || 'Token inválido ou erro na API do Tiny';
        toast.error(errorMsg);
        setTinyConnected(false);
        setTinyAccountInfo(null);
      }
    } catch (error) {
      console.error('Erro ao conectar ao Tiny ERP:', error);
      toast.error('Erro ao conectar ao Tiny ERP. Verifique sua conexão e tente novamente.');
    } finally {
      setTinyLoading(false);
    }
  };

  const handleDisconnectTiny = async () => {
    const confirmMessage = 'Tem certeza que deseja desconectar a integração com o Tiny ERP?\n\n⚠️ Isso também irá APAGAR os pedidos salvos do banco de dados.';
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      // O backend já deve lidar com a limpeza dos pedidos ao desconectar ou podemos forçar
      await authenticatedFetch('/api/tiny/pedidos', { method: 'DELETE' });

      // Depois desconectar a integração
      const response = await authenticatedFetch('/api/tiny/disconnect', {
        method: 'POST',
      });
      
      if (response.ok) {
        setTinyConnected(false);
        setTinyAccountInfo(null);
        toast.success('Integração com Tiny ERP removida com sucesso');
      }
    } catch (error) {
      console.error('Erro ao desconectar Tiny ERP:', error);
      toast.error('Erro ao desconectar Tiny ERP');
    }
  };

  return (
    <>
      <Header
        title="Tiny ERP"
        subtitle="Integração com Tiny ERP (Olist Tiny)"
      />

      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Card principal - Conexão */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <span className="text-indigo-600 text-xl">📦</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Integração com Tiny ERP (Olist Tiny)</h2>
                <p className="text-sm text-gray-500">Conecte sua conta do Tiny ERP para sincronizar dados e automatizar processos.</p>
              </div>
              <div className="ml-auto">
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                  tinyConnected
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {tinyConnected ? '● Conectado' : '○ Não configurado'}
                </span>
              </div>
            </div>

            {/* Configuração do Token */}
            <div className="border border-gray-200 rounded-lg p-5 mb-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Configuração do Token</h3>
              <p className="text-xs text-gray-500 mb-4">
                Para obter seu token de API do Tiny, acesse:{' '}
                <a
                  href="https://tiny.com.br/api-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 underline"
                >
                  Documentação do Tiny
                </a>
              </p>

              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="password"
                  value={tinyToken}
                  onChange={(e) => setTinyToken(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Token da API Tiny"
                />
                <button
                  onClick={handleConnectTiny}
                  disabled={tinyLoading || !tinyToken.trim()}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {tinyLoading ? 'Conectando...' : tinyConnected ? 'Reconectar' : 'Conectar'}
                </button>
                {tinyConnected && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleDisconnectTiny}
                      className="px-5 py-2 text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 whitespace-nowrap"
                    >
                      Desconectar
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-2">🔒 O token é armazenado de forma segura no servidor</p>
            </div>

            {/* Informações da Conta Conectada */}
            {tinyConnected && tinyAccountInfo && (
              <div className="border border-green-200 rounded-lg p-5 bg-green-50">
                <h3 className="text-sm font-semibold text-green-900 mb-4">Informações da Conta Conectada</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Nome da Empresa</p>
                    <p className="text-sm font-medium text-gray-900">{tinyAccountInfo.razao_social || tinyAccountInfo.fantasia || '-'}</p>
                  </div>
                  {tinyAccountInfo.email && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="text-sm font-medium text-gray-900">{tinyAccountInfo.email}</p>
                    </div>
                  )}
                  {tinyAccountInfo.cnpj_cpf && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">CPF/CNPJ</p>
                      <p className="text-sm font-medium text-gray-900">{tinyAccountInfo.cnpj_cpf}</p>
                    </div>
                  )}
                  {tinyAccountInfo.regime_tributario && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Regime Tributário</p>
                      <p className="text-sm font-medium text-gray-900">{tinyAccountInfo.regime_tributario}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}



